import { IFileInfoResponse } from '@kcftech/prometheus-client';
import { skipToken, useQuery } from '@tanstack/react-query';
import { sdClient } from 'contexts/SDKClientContext';
import { queryClient } from 'utils/query';
import { RecurseNonSdkClass } from 'utils/sdk_types';

function buildQueryKey(id: string | null) {
    return [`get-file-${id}`];
}

export type GetFileFromLibraryResult = RecurseNonSdkClass<
    Pick<
        IFileInfoResponse,
        | 'Id'
        | 'Name'
        | 'ImageMetadata'
        | 'ParentFolderId'
        | 'CreationDate'
        | 'UpdateDate'
        | 'Description'
        | 'LocationId'
        | 'CorporationId'
        | 'EntryType'
        | 'StorageFileName'
        | 'ThumbnailStorageFileName'
    >
> & {
    GroupIds: string[];
    MediaType: string | undefined;
};

async function getFileFromLibrary(id: string): Promise<GetFileFromLibraryResult> {
    const file = await sdClient.getFile({ id });
    return {
        Id: file.Id,
        Name: file.DisplayName,
        // this is definitively a file
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        StorageFileName: file.FileName!,
        ThumbnailStorageFileName: file.ThumbnailFileName,
        ImageMetadata: {
            Height: file.ImageHeight,
            Width: file.ImageWidth,
        },
        ParentFolderId: file.ParentFolderId,
        CreationDate: file.CreationDate,
        UpdateDate: file.UpdateDate,
        GroupIds: file.GroupIds,
        LocationId: file.Location?.LocationId,
        CorporationId: file.Corporation?.CorporationId,
        Description: file.Description,
        EntryType: file.FileType,
        MediaType: file.MediaType,
    };
}

export function useFileFromLibrary(id: string | null) {
    return useQuery({
        queryKey: buildQueryKey(id),

        queryFn: id ? () => getFileFromLibrary(id) : skipToken,
    });
}

export async function prefetchFileFromLibrary(id: string) {
    await queryClient.prefetchQuery({
        queryKey: buildQueryKey(id),
        queryFn: () => getFileFromLibrary(id),
    });
}

export async function setFileFromLibraryQueryData(
    id: string,
    data: GetFileFromLibraryResult,
) {
    await queryClient.setQueryData(buildQueryKey(id), data, {
        updatedAt: Date.now(),
    });
}
